import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Users, Plus, Trash2, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";

export default function ExternalCollaborators({ project, canEdit, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", send_invite: true });

  const list = project.external_collaborators || [];

  const add = async () => {
    if (!form.email.trim()) { toast.error("Email required"); return; }
    try {
      const { data } = await api.post(`/projects/${project.id}/external-collaborators`, form);
      if (data.invite_url) {
        toast.success(`Invited — magic-link sent to ${form.email}`);
      } else {
        toast.success(`Added ${form.email} to the project`);
      }
      setForm({ email: "", name: "", send_invite: true });
      setOpen(false);
      onUpdated && onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.email}?`)) return;
    try {
      await api.delete(`/projects/${project.id}/external-collaborators/${encodeURIComponent(c.email)}`);
      onUpdated && onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-flat p-5 mb-6" data-testid="external-collaborators">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-sm bg-slate-900 text-white flex items-center justify-center"><Users size={13} /></span>
          <div>
            <div className="label-overline">External collaborators</div>
            <p className="text-[11px] text-slate-500 mt-0.5">Freelancers, vendors, advisors — invited per project only.</p>
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="invite-external-btn">
                <Plus size={13} className="mr-1.5" />Invite
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-md">
              <DialogHeader><DialogTitle className="font-display">Invite external collaborator</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label className="label-overline">Name</Label><Input className="rounded-sm mt-1" placeholder="Jane Vendor" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ext-collab-name" /></div>
                <div><Label className="label-overline">Email</Label><Input type="email" className="rounded-sm mt-1" placeholder="jane@vendor.co" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ext-collab-email" /></div>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} data-testid="ext-collab-send" />
                  Send a magic-link invite email (7-day expiry)
                </label>
              </div>
              <DialogFooter>
                <Button onClick={add} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="ext-collab-save">
                  {form.send_invite ? <><Send size={13} className="mr-1" />Add &amp; invite</> : "Add"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {list.length === 0 ? (
        <div className="text-[11px] text-slate-400 text-center py-4" data-testid="external-collabs-empty">
          No external collaborators added yet.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="external-collabs-list">
          {list.map((c) => (
            <li key={c.email} className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 rounded-sm group" data-testid={`external-collab-${c.email}`}>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 truncate">{c.name || c.email}</div>
                <div className="text-[10px] text-slate-500 truncate flex items-center gap-1"><Mail size={9} />{c.email}{c.invited_at && <span className="text-slate-400"> · invited {formatDate(c.invited_at)}</span>}</div>
              </div>
              {canEdit && (
                <button onClick={() => remove(c)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600 transition-opacity" title="Remove" data-testid={`external-collab-remove-${c.email}`}>
                  <Trash2 size={11} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
